import { TrackItem, Sequence } from './model'
import { MediaProjectContainer } from './mediaProjectContainer'
import { Time } from '../media/time'

export class SequenceTools {
  totalLength = 0

  constructor (private sequence: Sequence, private project: MediaProjectContainer) {
  }

  getAdjacentItem (item, track, direction) {
    const index = track.items.indexOf(item)
    if (direction === 1 && index >= (track.items.length - 1)) {
      return null
    }
    if (direction === -1 && index === 0) {
      return null
    }
    const adjacent = track.items[index + direction]
    if (direction === 1 && adjacent.start.eq(item.end)) {
      return adjacent
    }
    if (direction === -1 && adjacent.end.eq(item.start)) {
      return adjacent
    }
    return null
  }

  getNextItem (item, track, direction) {
    const index = track.items.indexOf(item)
    if (direction === 1 && index >= (track.items.length - 1)) {
      return null
    }
    if (direction === -1 && index === 0) {
      return null
    }
    return track.items[index + direction]
  }

  getItemAtTime (time, inTracks) {
    if (!inTracks) {
      inTracks = this.sequence.tracks
    }
    for (const track of inTracks) {
      for (const item of track.items) {
        if (item.start.lte(time) && time.lt(item.end)) {
          return item
        }
      }
    }
  }

  getTrackForItem (item) {
    return this.sequence.tracks.find(x => x.items.includes(item))
  }

  getTrackIndexForItem (item) {
    return this.sequence.tracks.indexOf(this.getTrackForItem(item) as any)
  }

  rearrange () {
    const framerate = this.getFramerate()
    const errorThreshold = framerate ? framerate.inverse().div(1000).valueOf() : 0

    this.totalLength = Time.zero
    for (const track of this.sequence.tracks) {
      let lastT = Time.zero
      for (let i = 0; i < track.items.length; i++) {
        while ((i < track.items.length) && !track.items[i]) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete track.items[i]
        }
        const item = track.items[i]

        item.end = item.start.add(item.tOut).sub(item.tIn)

        item.start = item.start.mul(framerate).round().div(framerate).simplify(errorThreshold)
        item.end = item.end.mul(framerate).round().div(framerate).simplify(errorThreshold)
        item.tOut = item.tOut.mul(framerate).round().div(framerate).simplify(errorThreshold)
        item.tIn = item.tIn.mul(framerate).round().div(framerate).simplify(errorThreshold)

        lastT = item.end
      }

      track.items.sort((a, b) => a.start.sub(b.start).valueOf())
      track.items = track.items.filter(x => x.start.lt(x.end))

      if (track.items.length > 0) {
        lastT = track.items[track.items.length - 1].end
        this.totalLength = Time.max(this.totalLength, lastT)
      }
    }
  }

  getAvailableLeftTrim (item, track, skipAdjacentCheck) {
    const next = this.getNextItem(item, track, -1)
    let left = Time.min(item.start, item.tIn)
    let right = Time.zero

    const master = this.project.masterClipForItem(item)
    const asset = this.project.assetForItem(master)

    if (asset?.type === 'image') {
      left = item.start
      right = Time.infinity
    } else {
      right = item.tOut.sub(item.tIn)
    }

    if (next && !skipAdjacentCheck) {
      left = Time.min(left, item.start.sub(next.end))
    }

    return [left, right]
  }

  getAvailableRightTrim (item, track, skipAdjacentCheck) {
    const next = this.getNextItem(item, track, 1)
    let left = Time.zero
    let right = Time.zero

    const master = this.project.masterClipForItem(item)
    const asset = this.project.assetForItem(master)

    const duration = item.tOut.sub(item.tIn)
    if (asset?.type === 'image') {
      left = duration
      right = Time.infinity
      if (next && !skipAdjacentCheck) {
        right = duration.add(next.start.sub(item.end))
      }
    } else {
      left = duration
      right = item.tOut.sub(item.duration).neg()

      if (next && !skipAdjacentCheck) {
        right = Time.min(right, next.start.sub(item.end))
      }
    }

    return [left, right]
  }

  getAvailableLeftRoll (item, track) {
    const adjacent = this.getAdjacentItem(item, track, -1)
    const next = this.getNextItem(item, track, -1)
    let [left, right] = this.getAvailableLeftTrim(item, track, true)

    if (adjacent) {
      const [adjLeft, adjRight] = this.getAvailableRightTrim(adjacent, track, true)

      left = Time.min(left, adjLeft)
      right = Time.min(right, adjRight)
    } else if (next) {
      const emptySpace = item.start.sub(next.end)
      const nextDuration = next.tOut.sub(next.tIn)
      left = Time.min(left, emptySpace.add(nextDuration))
    }

    return [left, right]
  }

  getAvailableRightRoll (item, track) {
    const adjacent = this.getAdjacentItem(item, track, 1)
    const next = this.getNextItem(item, track, 1)
    let [left, right] = this.getAvailableRightTrim(item, track, true)

    if (adjacent) {
      const [adjLeft, adjRight] = this.getAvailableLeftTrim(adjacent, track, true)

      left = Time.min(left, adjLeft)
      right = Time.min(right, adjRight)
    } else if (next) {
      const emptySpace = next.start.sub(item.end)
      const nextDuration = next.tOut.sub(next.tIn)
      right = Time.min(right, emptySpace.add(nextDuration))
    }

    return [left, right]
  }

  trimLeft (item, _track, delta) {
    const oldIn = item.tIn
    const unclampedIn = item.tIn.add(delta)

    const master = this.project.masterClipForItem(item)
    const asset = this.project.assetForItem(master)
    const extendDuration = asset?.type === 'image'

    if (extendDuration) {
      const overflow = Time.min(Time.zero, unclampedIn).neg()

      item.tIn = unclampedIn.clamp(Time.zero, item.tOut)
      item.tOut = item.tOut.add(overflow)
      item.duration = item.tOut.sub(item.tIn)
      item.start = item.start.add(unclampedIn.sub(oldIn))
    } else {
      item.tIn = unclampedIn.clamp(Time.zero, item.tOut)
      item.start = item.start.add(item.tIn.sub(oldIn))
    }

    return item.tIn.sub(oldIn)
  }

  rippleClips (startTime, inclusive, delta) {
    this.rearrange()
    this.sequence.tracks.filter(x => !x.locked).forEach(track => {
      const items = track.items
        .filter(item => inclusive ? item.start.gte(startTime) : item.start.gt(startTime))

      if (delta.gt(Time.zero)) {
        items.reverse()
      }

      items.forEach(item => {
        this.overwriteInsert(item, track, item.start.add(delta))
        if (item.start.lt(Time.zero)) {
          item.tIn = item.tIn.sub(item.start)
          item.start = Time.zero
        }
      })
    })
  }

  rippleTrimLeft (item, delta) {
    const deltaIn = this.trimLeft(item, null, delta)
    this.rippleClips(item.start, true, deltaIn.neg())
  }

  rollTrimLeft (item, track, delta) {
    const adjacent = this.getAdjacentItem(item, track, -1)
    const deltaIn = this.trimLeft(item, track, delta)
    const next = this.getNextItem(item, track, -1)

    if (adjacent) {
      this.trimRight(next, track, deltaIn)
    } else if (next && item.start.lte(next.end)) {
      const deltaNext = Time.max(item.start.sub(next.end), delta)
      this.trimRight(next, track, deltaNext)
    }
  }

  trimRight (item, _track, delta) {
    const oldOut = item.tOut
    const master = this.project.masterClipForItem(item)
    const asset = this.project.assetForItem(master)
    const extendDuration = asset?.type === 'image'

    item.tOut = item.tOut.add(delta)
    if (extendDuration) {
      const deltaDuration = Time.max(Time.zero, item.tOut.sub(item.duration))
      item.duration = item.duration.add(deltaDuration)
    }
    item.tOut = item.tOut.clamp(item.tIn, item.duration)

    return item.tOut.sub(oldOut)
  }

  rippleTrimRight (item, delta) {
    const deltaOut = this.trimRight(item, null, delta)
    this.rippleClips(item.start, false, deltaOut)
  }

  rollTrimRight (item, track, delta) {
    const adjacent = this.getAdjacentItem(item, track, 1)
    const deltaOut = this.trimRight(item, track, delta)
    const next = this.getNextItem(item, track, 1)

    item.end = item.start.add(item.tOut).sub(item.tIn)

    if (adjacent) {
      this.trimLeft(adjacent, track, deltaOut)
    } else if (next?.start.lte(item.end)) {
      const deltaNext = Time.min(item.end.sub(next.start), delta)
      this.trimLeft(next, track, deltaNext)
    }
  }

  allFrameratesEqual () {
    const items = this.sequence.tracks.map(track => track.items).reduce((a, b) => a.concat(b), [])
    if (items.length === 0) {
      return true
    }
    const fps = this.getFramerate()
    return items.every(x => x.timebase.eq(fps))
  }

  getFramerate () {
    const items = this.sequence.tracks.map(track => track.items).reduce((a, b) => a.concat(b), [])
    if (items.length === 0) {
      return null
    }
    return items[0].timebase
  }

  rippleInsert (clip, targetTrack, time) {
    if (targetTrack.locked) {
      return
    }
    clip.start = time
    clip.end = clip.start.add(clip.tOut).sub(clip.tIn)
    for (const track of this.sequence.tracks.filter(x => !x.locked)) {
      const clipAtTime = track.items.find(x => x.start.lt(time) && x.end.gt(time))
      if (clipAtTime) {
        this.razorBlade(clipAtTime, time)
      }
    }
    this.rippleClips(time, true, clip.tOut.sub(clip.tIn))
    if (!targetTrack.items.includes(clip)) {
      targetTrack.items.push(clip)
    }
    this.rearrange()
  }

  overwriteInsert (clip, track, time) {
    if (track.locked) {
      return
    }
    clip.start = time
    clip.end = clip.start.add(clip.tOut).sub(clip.tIn)
    track.items.slice().filter(x => x !== clip).forEach(item => {
      if (item.start.gte(clip.start) && item.end.lte(clip.end)) {
        // inside
        track.items = track.items.filter(x => x !== item)
      } else if (item.start.gte(clip.start) && item.start.lt(clip.end) && item.end.gt(clip.end)) {
        // overlaps right
        item.tIn = item.tIn.add(clip.end).sub(item.start)
        item.start = clip.end
      } else if (item.start.lt(clip.start) && item.end.gt(clip.start) && item.end.lte(clip.end)) {
        // overlaps left
        item.tOut = item.tOut.sub(item.end).add(clip.start)
        item.end = clip.start
      } else if (item.start.lt(clip.start) && item.end.gt(clip.end)) {
        // covers
        const newItem = new TrackItem()
        Object.assign(newItem, item)
        delete (newItem as any).$$hashKey

        item.tOut = item.tOut.sub(item.end).add(clip.start)
        item.end = clip.start
        newItem.id = this.project.newID()
        newItem.tIn = newItem.tIn.add(clip.end).sub(item.start)
        newItem.start = clip.end
        track.items.push(newItem)
      }
    })
    if (!track.items.includes(clip)) {
      track.items.push(clip)
    }
    this.rearrange()
  }

  razorBlade (clip, time) {
    if (!clip) {
      return
    }

    const track = this.getTrackForItem(clip)
    if (track?.locked) {
      return
    }

    if (time.lte(clip.start) || time.gte(clip.end)) {
      return
    }
    const splitPoint = time.sub(clip.start)
    const newClip = new TrackItem()
    Object.assign(newClip, clip)
    delete (newClip as any).$$hashKey

    newClip.id = this.project.newID()
    newClip.tIn = clip.tIn.add(splitPoint)
    newClip.tOut = clip.tOut
    clip.tOut = newClip.tIn
    newClip.end = clip.end
    clip.end = clip.start.add(splitPoint)
    newClip.start = clip.end
    track?.items.splice(track.items.indexOf(clip) + 1, 0, newClip)
    this.rearrange()
  }

  rippleDelete (item) {
    const track = this.getTrackForItem(item)
    if (track) {
      if (track.locked) {
        return
      }
      track.items = track.items.filter(x => x !== item)
      this.rippleClips(item.start, true, item.start.sub(item.end))
    }
  }

  generateEmptySpaceItems () {
    const result: { start: Time, end: Time }[][] = []
    for (const track of this.sequence.tracks) {
      const current: { start: Time, end: Time }[] = []
      result.push(current)
      let lastEnd = Time.zero
      for (const item of track.items) {
        if (item.start.gt(lastEnd)) {
          current.push({
            start: lastEnd,
            end: item.start,
          })
        }
        lastEnd = item.end
      }
    }
    return result
  }
}
